import { lazy } from 'react';

// Route screens load as separate chunks (see App.js). A deploy replaces every
// chunk, so a tab opened before it asks for files that no longer exist the
// next time it navigates. Retry once in case it was a network blip, then
// reload the page to pick up the new build. Reloads are limited to one a
// minute per tab, so a chunk that is genuinely broken surfaces as an error
// instead of a reload loop.

export const RELOAD_KEY = 'hippo:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60 * 1000;
const RETRY_DELAY_MS = 1000;

// webpack names a failed script chunk ChunkLoadError; mini-css-extract-plugin
// flags a failed stylesheet chunk with this code.
export const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' || error?.code === 'CSS_CHUNK_LOAD_FAILED';

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function importWithRecovery(
  factory,
  {
    storage = getSessionStorage(),
    reload = () => window.location.reload(),
    now = Date.now,
    retryDelayMs = RETRY_DELAY_MS,
  } = {}
) {
  try {
    return await factory();
  } catch {
    await wait(retryDelayMs);
  }

  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error) || !storage) throw error;

    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(storage.getItem(RELOAD_KEY)) || 0;
    } catch {
      throw error;
    }
    if (now() - lastReloadAt < RELOAD_COOLDOWN_MS) throw error;

    try {
      storage.setItem(RELOAD_KEY, String(now()));
    } catch {
      throw error;
    }
    reload();
    // Keep the route suspended until the reload replaces the page.
    return new Promise(() => {});
  }
}

const lazyWithReload = (factory) => {
  const Component = lazy(() => importWithRecovery(factory));
  // Downloads the chunk ahead of the first render. A failure is ignored here:
  // the render loads it again and recovers as above.
  Component.preload = () => factory().catch(() => {});
  return Component;
};

export default lazyWithReload;
